import Core                 from '@/core/Core';
import ChangelogEntry       from '@/core/services/Changelogs/ChangelogEntry';
import ChangelogEntityDiff  from '@/core/services/Changelogs/ChangelogEntityDiff';
import ChangelogsDAO        from '@/core/services/Changelogs/ChangelogsDAO';
import TYPES                from '@/core/services/Changelogs/types';
import ENTITY_REGISTRY      from '@/core/services/Changelogs/EntityRegistry';
import ChangelogFormatters  from '@/core/services/Changelogs/ChangelogFormatters';

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import ParticipationDetailProduct
  from "@/domains/ParticipationDetailProduct/ParticipationDetailProduct";

/** Три события лога — других на беке нет. */
type EventType = 'create' | 'update' | 'delete';

/**
 * Сервис для работы с ченджлогами (историей изменений сущностей).
 */
class ChangelogsService {
    static EVENT_TYPE_LABELS: Record<EventType, string> = {
        create: 'Создано',
        update: 'Обновлено',
        delete: 'Удалено',
    };

    static EVENT_TYPE_ACTION_LABELS: Record<EventType, string> = {
        create: 'создал(а) ',
        update: 'обновил(а) ',
        delete: 'удалил(а) ',
    };

    /**
     * Убирает префикс сущности из ключа propertyDescriptor.
     * Бэкенд оперирует чистыми именами полей, а у дескрипторов префиксы
     * из-за parseChangelogEntityDiff в history.
     *
     * Пример: 'common_deliveryDays' -> 'deliveryDays'
     */
    static getCleanPropertyName(descriptorKey: string): string {
        if (descriptorKey.includes('_')) {
            return descriptorKey.split('_').slice(1).join('_');
        }
        else {
            return descriptorKey;
        }
    }

    /**
     * Применим ли фильтр "Изменения": нужны выбранные entityTypeName
     * и eventTypeName=update (колонка содержит данные только для update-событий).
     *
     * Используется и в сервисе (чтобы не слать мусор на бэк),
     * и в компоненте (чтобы задизейблить селектор).
     */
    static isChangesFilterApplicable(filters: {
        entityTypeName?: string | null;
        eventTypeName?:  string | null;
    }): boolean {
        if (filters.entityTypeName == null) {
            return false;
        }
        else if (filters.eventTypeName == null) {
            return false;
        }
        else {
            return filters.eventTypeName === 'update';
        }
    }

    /**
     * Загружает страницу ченджлогов для реестра через DataGridLoader.
     * Грид отдаёт скалярные id (см. extractOptionValue в VDataGridV2),
     * их же ждёт бэкенд — маппинг не нужен.
     */
    static async getListPaginated(
      params?: Parameters<typeof ChangelogsDAO.getList>[0] & {
        pagination?: RpcDataPagination;
        sort?: RpcDataSort;
      }
    ): Promise<{ data: ChangelogEntry[]; pagination: RpcDataPagination }> {
        const { pagination, sort, changes, ...filters } = params ?? {};

        // Фильтр "Изменения" отсекается на уровне сервиса, если не применим,
        // чтобы не слать мусор на бэк даже если грид оставил значение в памяти.
        let effectiveChanges: string | undefined;
        if (changes != null && this.isChangesFilterApplicable(filters)) {
            effectiveChanges = this.getCleanPropertyName(changes);
        }
        else {
            effectiveChanges = undefined;
        }

        const response = await ChangelogsDAO.getList(
            { ...filters, changes: effectiveChanges },
            pagination,
            sort,
        );

        return {
            data:       Core.utils.hydrateArray(response.data, ChangelogEntry.fromServer),
            pagination: response.meta.pagination,
        };
    }

    /**
     * В сервисе, а не в моделях, потому что парсинг используют две модели —
     * ChangelogEntry (общий реестр) и запись фида (FeedRecord с changelog-payload).
     * Дублировать switch на 20+ кейсов в обеих — значит руками их
     * синхронизировать. Модели тонкие, делегируют сюда.
     */
    static resolveChangeRecords(typeName: string, changes: ChangelogEntityDiff[]): string[] {
        if (changes.length === 0) return [];

        const entityClass = ENTITY_REGISTRY.get(typeName);
        if (!entityClass) return [];

        const descriptors = entityClass.getFieldDescriptors();

        return changes.map(change => {
          /**
           * Характеристики. Ключ изменения — не имя поля модели, а
           * characteristics[<название рамки>], поэтому дескриптора для него
           * в классе нет и общий разбор по типу поля не подходит: название
           * берём из самого ключа, значения форматируем своим парсером.
           * Проверяем до поиска дескриптора.
           */
          const characteristicMatch = change.propertyName.match(/^characteristics\[(.+)\]$/);
          if (characteristicMatch) {
            return ChangelogFormatters.parseCharacteristicDiff(characteristicMatch[1], change);
          }

          const descriptor = descriptors[change.propertyName];

          let messageString = '';

          // ===== Без декоратора — сырой вывод =====

          if (!descriptor) {
            return messageString + `<b>${change.propertyName}</b> с ${change.oldValue} на ${change.newValue}`;
          }

            // ===== Сборка префикса (Установлено/Изменено/Удалено) =====

            if (
                (change.oldValue === null || change.oldValue === '')
                && change.newValue !== null
            ) {
                messageString += 'Установлено ';
            } else if (
                change.oldValue !== null
                && change.newValue === null
                && descriptor.type !== TYPES.TENDER_PARTICIPATION // Костыль, чтобы обработать участие (participation === null говорит о том что учавствует, но еще не сделал ставку)
            ) {
                return `Удалено <b>${descriptor ? descriptor.title : change.propertyName}</b>`;
            } else {
                messageString += 'Изменено ';
            }

            // ===== Без декоратора — сырой вывод =====

            if (!descriptor) {
                return messageString + `<b>${change.propertyName}</b> с ${change.oldValue} на ${change.newValue}`;
            }

            messageString += `<b>${descriptor.title}</b>`;

            // ===== Кастомный resolver — приоритет над типами =====

            if (typeof descriptor.resolver === 'function') {
                return messageString + descriptor.resolver(change.oldValue, change.newValue);
            }

            if (descriptor.title === 'комментарий' && descriptor.type === 'string') {
                return ChangelogFormatters.parseCommentDiff(change);
            }

            // ===== Парсинг по типу поля =====

            switch (descriptor.type) {
                case TYPES.INT_RANGE:            return messageString + ChangelogFormatters.parseIntRangeDiff(change);
                case TYPES.DATE_RANGE:           return messageString + ChangelogFormatters.parseDateRangeDiff(change);
                case TYPES.STRING:
                case TYPES.NUMBER:               return messageString + ChangelogFormatters.parsePrimitiveDiff(change);
                case TYPES.NUMBER_PERCENT:       return messageString + ChangelogFormatters.parsePrimitiveDiffWithPercentSign(change);
                case TYPES.PRICE:                return messageString + ChangelogFormatters.parsePriceDiff(change);
                case TYPES.TENDER_BID:           return messageString + ChangelogFormatters.parseTenderBid(change);
                /** Решили что все цены касающиеся тендера нужно форматировать 3 знаками после запятой */
                case TYPES.TENDER_PRICE:         return messageString + ChangelogFormatters.parseTenderPriceDiff(change);
                case TYPES.SUM:                  return messageString + ChangelogFormatters.parseSumDiff(change);
                case TYPES.DATE:                 return messageString + ChangelogFormatters.parseDateDiff(change);
                case TYPES.DATE_TIME:            return messageString + ChangelogFormatters.parseDateTimeDiff(change);
                case TYPES.BOOLEAN:              return messageString;
                case TYPES.CUSTOM_BOOLEAN:       return ChangelogFormatters.parseCustomBoolean(change, descriptor.title);
                case TYPES.OBJECT:               return messageString + ChangelogFormatters.parseObjectDiff(change);
                case TYPES.PAYMENT_TERM:         return messageString + ChangelogFormatters.parsePaymentTerm(change);
                case TYPES.SUPPLY_OFFER:         return messageString + ChangelogFormatters.parseSupplyOffer(change);
                case TYPES.MANUFACTURERS:        return ChangelogFormatters.parseManufacturers(change); /** messageString не нужен — массив, и удаление/добавление всегда update */
                case TYPES.COORDINATES:          return messageString + ChangelogFormatters.parseCoordinates(change);
                case TYPES.TENDER_PARTICIPATION: return ChangelogFormatters.parseTenderParticipation(change);
                case TYPES.REFUSAL_REASONS:      return messageString + ParticipationDetailProduct.parseRefusalReasons(change);
                case TYPES.TARIFFS:              return ChangelogFormatters.parseTariffDiff(change);
            }

            return messageString;
        });
    }

    /**
     * Разрешить человекочитаемое название типа сущности по backend type name.
     * Возвращает значение из @Title_ru_RU_Class декоратора, либо null если класса нет в реестре.
     */
    static resolveEntityTypeName(typeName: string): string | null {
        const entityClass = ENTITY_REGISTRY.get(typeName);
        return entityClass?.entityTypeName ?? null;
    }
}

export default ChangelogsService;
